"use client"

import React from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts"
import { PG_EVALUATION_RUNS } from "../data/pg_runs_all"

const NOISE_LEVELS = [0, 10, 20, 30, 40]

// Colors for each method
const METHOD_COLORS = {
  "SchemaI": "#3b82f6",      // blue
  "GMM": "#ef4444",          // red
  "PG-HIVE-ELSH": "#f59e0b", // orange/yellow
  "PG-HIVE-MinHash": "#10b981" // green
}

// Transform data for recharts format
function prepareChartData(dataObj) {
  return NOISE_LEVELS.map((noise, idx) => {
    const point = { noise }
    Object.keys(dataObj).forEach(method => {
      point[method] = dataObj[method][idx]
    })
    return point
  })
}

function SingleChart({ data, title, yLabel, showMethods }) {
  const chartData = prepareChartData(data)

  return (
    <div className="mb-3 w-full">
      <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 text-center">
        {title}
      </h4>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="noise"
            label={{ value: 'Noise (%)', position: 'insideBottom', offset: -5, fontSize: 12 }}
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
          />
          <YAxis
            domain={[0.4, 1.0]}
            ticks={[0.4, 0.6, 0.8, 1.0]}
            label={{ value: yLabel, angle: -90, position: 'insideLeft', fontSize: 12 }}
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
            width={35}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              fontSize: '13px'
            }}
            formatter={(value) => value.toFixed(2)}
          />
          {showMethods.includes("SchemaI") && (
            <Line
              type="monotone"
              dataKey="SchemaI"
              stroke={METHOD_COLORS.SchemaI}
              strokeWidth={1.5}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          )}
          {showMethods.includes("GMM") && (
            <Line
              type="monotone"
              dataKey="GMM"
              stroke={METHOD_COLORS.GMM}
              strokeWidth={1.5}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          )}
          {showMethods.includes("PG-HIVE-ELSH") && (
            <Line
              type="monotone"
              dataKey="PG-HIVE-ELSH"
              stroke={METHOD_COLORS["PG-HIVE-ELSH"]}
              strokeWidth={1.5}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          )}
          {showMethods.includes("PG-HIVE-MinHash") && (
            <Line
              type="monotone"
              dataKey="PG-HIVE-MinHash"
              stroke={METHOD_COLORS["PG-HIVE-MinHash"]}
              strokeWidth={1.5}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function EvaluationCharts({ dataset }) {
  const evaluationData = PG_EVALUATION_RUNS[dataset]
  const data = evaluationData?.detailed?.charts

  if (!data) {
    return (
      <div className="text-center text-red-600 py-8">
        No evaluation chart data available for {dataset}
      </div>
    )
  }

  return (
    <div className="space-y-4 w-full">
      {/* Title */}
      <h2 className="text-xl font-bold text-center text-gray-900 dark:text-slate-100">
        {dataset === 'HETIO' ? 'HET.IO' : dataset}
      </h2>

      {/* Legend at top */}
      <div className="flex justify-center items-center gap-4 pb-3 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0.5" style={{ backgroundColor: METHOD_COLORS.SchemaI }}></div>
          <span className="text-sm text-gray-700 dark:text-slate-300">SchemaI</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0.5" style={{ backgroundColor: METHOD_COLORS.GMM }}></div>
          <span className="text-sm text-gray-700 dark:text-slate-300">GMM</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0.5" style={{ backgroundColor: METHOD_COLORS["PG-HIVE-ELSH"] }}></div>
          <span className="text-sm text-gray-700 dark:text-slate-300">PG-HIVE-ELSH</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0.5" style={{ backgroundColor: METHOD_COLORS["PG-HIVE-MinHash"] }}></div>
          <span className="text-sm text-gray-700 dark:text-slate-300">PG-HIVE-MinHash</span>
        </div>
      </div>

      {/* 3 columns layout */}
      <div className="grid grid-cols-3 gap-4">
        {/* 100% Label Information - Left Column */}
        <div className="border-l-4 border-blue-500 pl-3">
          <h3 className="text-sm font-semibold mb-2 text-gray-800 dark:text-slate-200">
            100% label information
          </h3>
          <div className="space-y-2">
            <SingleChart
              data={data["100"].nodes}
              title="Nodes F1"
              yLabel="F1"
              showMethods={["SchemaI", "GMM", "PG-HIVE-ELSH", "PG-HIVE-MinHash"]}
            />
            <SingleChart
              data={data["100"].edges}
              title="Edges F1"
              yLabel="F1"
              showMethods={["SchemaI", "PG-HIVE-ELSH", "PG-HIVE-MinHash"]}
            />
          </div>
        </div>

        {/* 50% Label Information - Middle Column */}
        <div className="border-l-4 border-amber-500 pl-3">
          <h3 className="text-sm font-semibold mb-2 text-gray-800 dark:text-slate-200">
            50% label information
          </h3>
          <div className="space-y-2">
            <SingleChart
              data={data["50"].nodes}
              title="Nodes F1"
              yLabel="F1"
              showMethods={["PG-HIVE-ELSH", "PG-HIVE-MinHash"]}
            />
            <SingleChart
              data={data["50"].edges}
              title="Edges F1"
              yLabel="F1"
              showMethods={["PG-HIVE-ELSH", "PG-HIVE-MinHash"]}
            />
          </div>
        </div>

        {/* 0% Label Information - Right Column */}
        <div className="border-l-4 border-green-500 pl-3">
          <h3 className="text-sm font-semibold mb-2 text-gray-800 dark:text-slate-200">
            0% label information
          </h3>
          <div className="space-y-2">
            <SingleChart
              data={data["0"].nodes}
              title="Nodes F1"
              yLabel="F1"
              showMethods={["PG-HIVE-ELSH", "PG-HIVE-MinHash"]}
            />
            <SingleChart
              data={data["0"].edges}
              title="Edges F1"
              yLabel="F1"
              showMethods={["PG-HIVE-ELSH", "PG-HIVE-MinHash"]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
